import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import TopicDictationCard from './TopicDictationCard';
import { useSessionState } from '../../session/hooks/useSessionState';
import { SrsFeedback, WritingContent } from '../../db/indexedDb';
import { deriveWritingSrsAdjustments } from '../paragraphRecall/srsAdjustments';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';

/**
 * 话题默写页面 - SRS 模式
 * 使用 SessionController 管理 SRS 状态，支持 again/hard/good 反馈
 */
export default function TopicDictationPage() {
  // 使用 writing 类型过滤，并且只显示 topic-dictation 模式的卡片
  const { state, actions, controllerRef } = useSessionState('writing', false, 'topic-dictation', {
    writingModeOverride: 'static',
  });
  const {
    loadState, snapshot, saving, error, cardError, renderTick, ended,
    topicDictationResult, hasNewAction,
  } = state;

  const handleFeedback = async (feedback: SrsFeedback) => {
    if (saving || ended) return;
    const controller = controllerRef.current;
    if (!controller) return;

    actions.setSaving(true);
    actions.setError(undefined);
    actions.setCardError(null);

    try {
      let reviewOptions;

      if (topicDictationResult) {
        reviewOptions = {
          notes: `topic-dictation:${topicDictationResult.averageCoverage}`,
          skillConfig: deriveWritingSrsAdjustments(topicDictationResult.averageCoverage),
          answerMeta: {
            topicDictationResult: {
              averageCoverage: topicDictationResult.averageCoverage,
              recallRatio: topicDictationResult.recallRatio,
              sentenceCount: topicDictationResult.sentenceScores?.length ?? 0,
            },
          },
        };
      }

      // 记录学习时长
      if (activeCard) {
        await actions.logStudyTime(
          activeCard.id,
          activeCard.type,
          activeCard.mode,
          feedback,
          topicDictationResult?.averageCoverage
        );
      }

      await controller.submit(feedback, reviewOptions);
      const newSnap = controller.snapshot();
      actions.setSnapshot(newSnap);

      // 重置卡片状态
      actions.setTopicDictationResult(null);
      actions.setHasNewAction(false);

      if (newSnap.finished) {
        actions.setEnded(true);
      }
    } catch (err) {
      actions.setError(err instanceof Error ? err.message : '提交失败');
    } finally {
      actions.setSaving(false);
    }
  };

  const handleGoBack = async () => {
    const controller = controllerRef.current;
    if (!controller || saving) return;

    controller.goBack();
    const newSnap = controller.snapshot();
    actions.setSnapshot(newSnap);
    actions.setTopicDictationResult(null);
    actions.setHasNewAction(false);
    actions.setRenderTick(t => t + 1);
  };

  const handleAdvanceWithoutUpdate = async () => {
    const controller = controllerRef.current;
    if (!controller || saving) return;

    controller.advanceWithoutUpdate();
    const newSnap = controller.snapshot();
    actions.setSnapshot(newSnap);
    actions.setTopicDictationResult(null);
    actions.setHasNewAction(false);
  };

  const activeCard = snapshot?.currentCard;
  const total = snapshot?.total ?? 0;
  const completed = snapshot?.completed ?? 0;
  const currentIndex = snapshot?.currentIndex ?? 0;
  const isFinished = Boolean(snapshot?.finished);
  const isCurrentSubmitted = controllerRef.current?.isCurrentSubmitted() ?? false;
  const canGoBack = currentIndex > 0 && !isFinished;

  // 只有 topic-dictation 模式的卡片才显示
  const isTopicDictationCard = activeCard?.type === 'writing' && activeCard?.mode === 'topic-dictation';
  const feedbackDisabled = !activeCard || saving || !!cardError || !topicDictationResult;

  // 键盘快捷键
  useKeyboardShortcuts({
    shortcuts: {
      '1': () => !feedbackDisabled && handleFeedback('again'),
      '2': () => !feedbackDisabled && handleFeedback('hard'),
      '3': () => !feedbackDisabled && handleFeedback('good'),
      'arrowleft': () => canGoBack && !saving && handleGoBack(),
      'arrowright': () => isCurrentSubmitted && !hasNewAction && !saving && handleAdvanceWithoutUpdate(),
    },
    enabled: !isFinished && !!activeCard && isTopicDictationCard,
  });

  const loadMessage = useMemo(() => {
    if (loadState.status === 'loading') return '正在加载……';
    if (loadState.status === 'error') return loadState.message;
    if (ended) return '已完成本次练习';
    if (isFinished && total === 0) return '本次队列为空';
    if (total === 0) return '今日暂无到期的话题默写卡片';
    // 防御性检查：如果当前卡片不是 topic-dictation 模式（理论上不应该发生）
    if (activeCard && !isTopicDictationCard) {
      console.warn('[TopicDictationPage] 意外收到非 topic-dictation 卡片:', activeCard);
      return '数据异常，请刷新页面重试';
    }
    return '';
  }, [ended, isFinished, loadState, total, activeCard, isTopicDictationCard]);

  return (
    <div className="session-layout">
      {/* Header */}
      <header className="session-header">
        <Link to="/today" className="session-back">←</Link>
        <span className="session-title">话题默写 · SRS</span>
        <span className="session-count">{Math.min(currentIndex + 1, total)}/{total}</span>
      </header>

      {/* Progress Bar */}
      {!isFinished && total > 0 && (
        <div className="session-progress-bar">
          <div className="progress-track">
            <div
              className="progress-fill"
              style={{ width: total ? `${(completed / total) * 100}%` : '0%' }}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="session-content">
        {loadMessage && (
          <div className="empty-state">
            <div className="empty-state-icon">📝</div>
            <div className="empty-state-title">{loadMessage}</div>
            {(isFinished || total === 0) && (
              <Link className="btn btn-primary" to="/today" style={{ marginTop: '1rem' }}>
                返回首页
              </Link>
            )}
          </div>
        )}

        {error && (
          <div className="card" style={{ background: 'var(--accent-red)' }}>
            <div className="card-body">
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* 完成状态 */}
        {isFinished && total > 0 && (
          <div className="session-complete">
            <div className="session-complete-icon">✓</div>
            <div className="session-complete-title">练习完成！</div>
            <div className="session-complete-stats">
              <div className="session-complete-stat">{completed}/{total}</div>
              <div className="session-complete-label">CARDS REVIEWED</div>
            </div>
            <Link className="btn btn-primary" to="/today">返回首页 →</Link>
          </div>
        )}

        {/* 卡片内容 */}
        {!isFinished && activeCard && isTopicDictationCard && (
          <TopicDictationCard
            key={`${activeCard.id}-${renderTick}`}
            card={activeCard}
            onEvaluated={actions.setTopicDictationResult}
          />
        )}
      </div>

      {/* Feedback Buttons */}
      {!isFinished && activeCard && isTopicDictationCard && (
        <div className="session-footer">
          <div className="session-nav-row">
            <button
              className="session-nav-btn"
              onClick={handleGoBack}
              disabled={!canGoBack || saving}
              title="上一张"
            >
              ← 上一张
            </button>
            {isCurrentSubmitted && !hasNewAction && (
              <button
                className="session-nav-btn"
                onClick={handleAdvanceWithoutUpdate}
                disabled={saving}
                title="跳过到下一张（不更新数据）"
              >
                下一张 →
              </button>
            )}
          </div>
          <div className="feedback-group">
            <button
              className="feedback-btn again"
              onClick={() => handleFeedback('again')}
              disabled={feedbackDisabled}
            >
              <span>AGAIN</span>
              <span className="interval">&lt;1d</span>
            </button>
            <button
              className="feedback-btn hard"
              onClick={() => handleFeedback('hard')}
              disabled={feedbackDisabled}
            >
              <span>HARD</span>
              <span className="interval">1d</span>
            </button>
            <button
              className="feedback-btn good"
              onClick={() => handleFeedback('good')}
              disabled={feedbackDisabled}
            >
              <span>GOOD</span>
              <span className="interval">3d</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
